/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { FC, ReactNode, ReactElement, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@airwallex/global';
import { Label, Body, Headline } from '@airwallex/typography';

export interface CurrencyFieldProps {
  className?: string;
  label: ReactNode;
  currency?: string;
  amount?: number;
  type?: 'primary' | 'secondary' | 'primary-small';
  customValue?: ReactNode;
}

const CurrencyField: FC<CurrencyFieldProps> = ({
  className,
  label,
  currency,
  amount,
  customValue,
  type = 'secondary',
}) => {
  const currencyInfo =
    customValue ||
    formatCurrency({
      amount,
      currency,
      showCode: true,
    });
  let currencyElement: JSX.Element;
  if (type === 'primary') {
    currencyElement = <Headline variant="200">{currencyInfo}</Headline>;
  } else if (type === 'primary-small') {
    currencyElement = <Headline variant="500">{currencyInfo}</Headline>;
  } else {
    currencyElement = <Body variant="100.Subtle">{currencyInfo}</Body>;
  }
  return (
    <div
      className={className}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <Label variant="100">{label}</Label>
      {currencyElement}
    </div>
  );
};

export interface CurrencyFieldsGroupProps {
  className?: string;
  children: ReactElement<CurrencyFieldProps> | ReactElement<CurrencyFieldProps>[];
}

export const CurrencyFieldsGroup: FC<CurrencyFieldsGroupProps> = ({ className, children }) => {
  return (
    <div
      className={className}
      css={css`
        display: flex;
        flex-direction: column;
        /* space between rows, add a css attribute to this component to override the value */
        row-gap: 10px;
      `}
    >
      {children}
    </div>
  );
};

export interface SurchargeCurrencyFieldsGroupProps {
  className?: string;
  amount: number;
  surcharge?: number;
  currency?: string;
  amountLabel?: ReactNode;
  surchargeLabel?: ReactNode;
  totalLabel?: ReactNode;
  totalType?: CurrencyFieldProps['type'];
}

export const SurchargeCurrencyFieldsGroup: FC<SurchargeCurrencyFieldsGroupProps> = ({
  className,
  amount,
  surcharge,
  currency,
  amountLabel,
  surchargeLabel,
  totalLabel,
  totalType = 'primary',
}) => {
  const { t } = useTranslation();
  const totalAmount = useMemo(() => {
    return amount + (surcharge || 0);
  }, [amount, surcharge]);
  return (
    <CurrencyFieldsGroup className={className}>
      <CurrencyField
        label={amountLabel ?? t('checkout.order_amount')}
        currency={currency}
        amount={amount}
        type={surcharge ? 'secondary' : totalType}
      />
      {!!surcharge && (
        <Fragment>
          <CurrencyField
            label={surchargeLabel ?? t('checkout.processing_fee')}
            currency={currency}
            amount={surcharge}
            type="secondary"
          />
          <CurrencyField
            label={totalLabel ?? t('checkout.total_to_pay')}
            currency={currency}
            amount={totalAmount}
            type={totalType}
          />
        </Fragment>
      )}
    </CurrencyFieldsGroup>
  );
};

export default CurrencyField;
